import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { getPendingRef, clearPendingRef } from '../utils/installPwa';
import { resolveDestination } from '../utils/safeRedirect';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Terminal, Eye, EyeOff, CheckCircle2, XCircle, Gift } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  // Phase 7-lite — fall back to sessionStorage if the QR's ?ref=ABC param
  // got dropped (e.g. mis-tap on the install banner redirected the user to
  // a clean /register URL). capturePendingRef() runs on app boot.
  const initialCode = (
    searchParams.get('ref') ||
    searchParams.get('staff') ||
    getPendingRef() ||
    ''
  ).toUpperCase();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'customer',
    phone: '',
    address: '',
    referral_code: initialCode,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Referral code validation state
  const [refState, setRefState] = useState({ valid: null, owner: null, role: null });
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Live-validate the referral code (debounced)
  useEffect(() => {
    const code = (formData.referral_code || '').trim().toUpperCase();
    if (!code) {
      setRefState({ valid: null, owner: null, role: null });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/referrals/validate`, { code });
        if (data.valid) {
          setRefState({ valid: true, owner: data.owner_first_name, role: data.owner_role });
        } else {
          setRefState({ valid: false, owner: null, role: null });
        }
      } catch {
        setRefState({ valid: false, owner: null, role: null });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [formData.referral_code]);

  const handleChange = (e) => {
    const next = e.target.name === 'referral_code'
      ? e.target.value.toUpperCase()
      : e.target.value;
    setFormData(prev => ({ ...prev, [e.target.name]: next }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Phase 6.2.2 — Customer must pick from autocomplete (lock-until-selected).
    // Address is now a structured object once a Mapbox suggestion is selected;
    // a flat string means they typed but didn't pick. Block submit so we don't
    // silently capture typos that fail at checkout 10 minutes later.
    if (formData.role === 'customer') {
      const a = formData.address;
      const valid = a && typeof a === 'object' && a.line1 && a.city && a.state && a.postcode;
      if (!valid) {
        setError('Please pick your address from the dropdown so we deliver to the right place');
        return;
      }
    }

    setLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      // Phase 5.2.2a — referral_code is now required server-side; submit as-is
      const user = await register(userData);
      // Phase 7-lite — registration succeeded, no longer need the cached ref.
      clearPendingRef();
      // Phase 1 (Feb 2026) — Redirect-aware register. Same rules as login:
      // honour a validated `?next=` / state.from destination, otherwise land
      // on the role dashboard.
      const dest = resolveDestination(location, user.role);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] flex items-start sm:items-center justify-center p-4 pb-32 scanline-overlay">{/* Phase 5.9.27 — pb-32 reserves clearance so the floating PWA install pill (fixed bottom-20) doesn't overlap CREATE ACCOUNT */}
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2">
            <Terminal className="w-6 h-6 text-[#39FF14]" />
            <span className="text-[#39FF14] font-mono text-xl font-bold neon-text">ASH&MERCH_</span>
          </div>
          <p className="text-[#4B6B52] font-mono text-sm mt-2">// NEW USER REGISTRATION</p>
        </div>

        {/* Register Form */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
          <div className="border-b border-[#2A0F3D] pb-4 mb-6">
            <h1 className="text-[#39FF14] font-mono text-lg font-bold uppercase tracking-wider">
              {'>'} REGISTER_
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Phase 5.9.28 — when the user arrived via a referral, replace the
                generic "// INVITE ONLY" banner with a warmer "joining via X's
                invite" pill. The referral_code input is hidden entirely below
                (the value still ships in formData). When no referral context,
                fall back to the original banner. */}
            {initialCode ? (
              <div
                className="bg-[rgba(57,255,20,0.08)] border border-[#39FF14] p-2 text-center"
                data-testid="referral-pill"
              >
                <p className="text-[#39FF14] font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                  <Gift className="w-3 h-3" />
                  {refState.valid === true && refState.owner
                    ? <>Joining via <span className="text-[#39FF14] font-bold">{refState.owner}</span>&apos;s invite</>
                    : refState.valid === false
                      ? <span className="text-[#FF3B30]">// Invalid referral code: {initialCode}</span>
                      : <>Referral: <span className="text-[#39FF14] font-bold">{initialCode}</span></>
                  }
                </p>
              </div>
            ) : (
              <div className="bg-[rgba(255,159,10,0.08)] border border-[#FF9F0A] p-2 text-center" data-testid="invite-only-banner">
                <p className="text-[#FF9F0A] font-mono text-xs uppercase tracking-widest">// INVITE ONLY</p>
              </div>
            )}

            {error && (
              <div className="bg-[rgba(255,59,48,0.1)] border border-[#FF3B30] p-3 text-[#FF3B30] font-mono text-sm" data-testid="register-error">
                [ERROR] {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                FULL_NAME *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                placeholder="John Doe"
                required
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                EMAIL_ADDRESS *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                placeholder="user@ashandmerch.com"
                required
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                ACCOUNT_TYPE *
              </Label>
              <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono focus:ring-[#39FF14] rounded-none" data-testid="register-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                  <SelectItem value="customer" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">CUSTOMER</SelectItem>
                  <SelectItem value="driver" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">DRIVER</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                  PASSWORD *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none pr-10"
                    placeholder="••••••"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B6B52] hover:text-[#39FF14]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                  CONFIRM *
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                  placeholder="••••••"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                PHONE_NUMBER
              </Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                placeholder="555-0100"
                data-testid="register-phone"
              />
            </div>

            {formData.role === 'customer' && (
              <div className="space-y-2">
                <Label htmlFor="address" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                  DELIVERY_ADDRESS
                </Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                  placeholder="Start typing your address..."
                  data-testid="register-address"
                />
              </div>
            )}

            {/* Phase 5.9.28 — when the signup originated from a referral
                link, the code is already in `formData.referral_code` and shown
                in the pill at the top. Hide the editable input to keep the
                form compact and reinforce the "you were invited" feel. */}
            {initialCode ? (
              <input type="hidden" name="referral_code" value={formData.referral_code} data-testid="register-referral-code-hidden" />
            ) : (
              <div className="space-y-2" data-testid="referral-code-block">
                <Label htmlFor="referral_code" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider flex items-center gap-2">
                  <Gift className="w-3 h-3" /> REFERRAL_CODE *
                </Label>
                <Input
                  id="referral_code"
                  name="referral_code"
                  value={formData.referral_code}
                  onChange={handleChange}
                  className={`bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none uppercase tracking-widest ${
                    refState.valid === true ? 'border-[#39FF14]' : refState.valid === false ? 'border-[#FF3B30]' : ''
                  }`}
                  placeholder="JANE420"
                  maxLength={16}
                  required
                  data-testid="register-referral-code"
                />
                {refState.valid === true && (
                  <div className="text-[#39FF14] font-mono text-xs flex items-center gap-1" data-testid="referral-valid">
                    <CheckCircle2 className="w-3 h-3" />
                    // Referred by {refState.owner}
                  </div>
                )}
                {refState.valid === false && (
                  <div className="text-[#FF3B30] font-mono text-xs flex items-center gap-1" data-testid="referral-invalid">
                    <XCircle className="w-3 h-3" />
                    // Invalid referral code
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] hover:shadow-[0_0_12px_rgba(57,255,20,0.6)] transition-all uppercase tracking-wider rounded-none h-12 mt-6"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="cursor-blink">█</span> PROCESSING...
                </span>
              ) : (
                '> CREATE ACCOUNT'
              )}
            </Button>
          </form>

          {/* Phase 5.9.27 — hide the "EXISTING USER? LOGIN_HERE" link when
              the signup is arrived-at via a referral link. A referrer expects
              the recipient to sign up, not bounce to login. `initialCode` is
              populated from ?ref= / ?staff= / sessionStorage so this captures
              every entry path. */}
          {!initialCode && (
            <div className="mt-6 pt-6 border-t border-[#2A0F3D] text-center">
              <p className="text-[#4B6B52] font-mono text-sm">
                // EXISTING USER?{' '}
                <Link to="/login" className="text-[#39FF14] hover:underline" data-testid="login-link">
                  LOGIN_HERE
                </Link>
              </p>
            </div>
          )}
          {/* Phase 6.0 — Privacy / Terms agreement line hidden at operator
              request pending legal review. Routes /privacy and /terms still
              resolve for direct access.
          <p className="text-[#4B6B52] font-mono text-[10px] mt-3 text-center">
            By registering you agree to our{' '}
            <Link to="/terms" className="hover:text-[#39FF14] hover:underline" data-testid="terms-link">TERMS</Link>
            {' and '}
            <Link to="/privacy" className="hover:text-[#39FF14] hover:underline" data-testid="privacy-link">PRIVACY POLICY</Link>.
          </p>
          */}
        </div>
      </div>
    </div>
  );
}
